import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Tabs, Tab, Card, CardContent, Grid, Chip,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Divider,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  AccountBalance, CheckCircle, ArrowForward, Storefront,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  getWithdrawalOptions, getMyWithdrawalRequests, createCashWithdrawalRequest, cancelWithdrawalRequest,
} from 'api/withdrawal';
import { ClientGetStationList } from 'api/client';

const CURRENCIES = ['LAK', 'THB', 'USD'];
const SYM = { USD: '$', THB: '฿', LAK: '₭' };
const fmt = (a, c) => `${SYM[c] || ''}${Number(a || 0).toLocaleString()}`;
const STATUS_COLOR = { pending: 'warning', approved: 'success', paid: 'success', rejected: 'error', cancelled: 'default', expired: 'default', verified: 'success' };
const stationName = (st, lang) => (st ? (lang === 'lo' && st.nameLA ? st.nameLA : st.name) : '');

const WalletWithdrawal = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const flash = (msg, isErr) => { if (isErr) { setError(msg); setSuccess(''); } else { setSuccess(msg); setError(''); } };

  return (
    <Box m={{ xs: 1.5, sm: 3 }} maxWidth={900} mx="auto">
      <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, mb: 2 }}>
        <AccountBalance sx={{ mr: 1 }} color="secondary" />
        {t('wallet.withdrawal.title')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} textColor="secondary" indicatorColor="secondary" variant={isMobile ? 'fullWidth' : 'standard'} sx={{ mb: 2 }}>
        <Tab label={t('wallet.withdrawal.withdraw')} />
        <Tab label={t('wallet.withdrawal.history')} />
      </Tabs>

      {tab === 0 && <WithdrawTab t={t} flash={flash} goHistory={() => setTab(1)} />}
      {tab === 1 && <HistoryTab t={t} flash={flash} />}
    </Box>
  );
};

// ─── Withdraw (cash at a branch) ─────────────────────────────────────────────
const WithdrawTab = ({ t, flash, goHistory }) => {
  const lang = (useTranslation().i18n.language || 'en').split('-')[0];
  const [options, setOptions] = useState(null);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('THB');
  const [amount, setAmount] = useState('');
  const [stationId, setStationId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, st] = await Promise.all([getWithdrawalOptions(), ClientGetStationList()]);
      setOptions(r.data);
      setStations(st?.data || []);
      const first = CURRENCIES.find((c) => r.data.currencies[c]?.canWithdraw);
      if (first) setCurrency(first);
      // Default branch: the customer's usual pickup point when it's a branch.
      let def = '';
      try { def = JSON.parse(localStorage.getItem('customer') || '{}')?.default_route || ''; } catch { /* ignore */ }
      setStationId((prev) => prev || ((st?.data || []).some((x) => x.id === def) ? def : ''));
    } catch (e) { flash(e.message || 'Failed to load', true); }
    finally { setLoading(false); }
  }, [flash]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setAmount(''); }, [currency]);

  const info = options?.currencies[currency];
  const num = Number(amount);
  const amountOk = info && num > 0 && num <= info.maxAmount && (num >= (info.minAmount ?? 0) || num === info.available);
  const station = stations.find((x) => x.id === stationId);

  const submit = async () => {
    setSubmitting(true);
    try {
      await createCashWithdrawalRequest(currency, num, stationId);
      flash(t('wallet.withdrawal.cash.submitted'), false);
      setConfirmOpen(false);
      goHistory();
    } catch (e) { flash(e.message || 'Failed', true); setConfirmOpen(false); }
    finally { setSubmitting(false); }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress color="secondary" /></Box>;

  const hold = options?.securityHold;

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Security cooling-off after a recent password / phone-number change */}
      {hold?.held && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('wallet.withdrawal.securityHold', {
            until: new Date(hold.until).toLocaleString(),
          })}
        </Alert>
      )}

      <Alert severity="info" icon={<Storefront />} sx={{ mb: 2 }}>{t('wallet.withdrawal.cash.intro')}</Alert>

      {/* Currency selector */}
      <Typography variant="overline" color="text.secondary">{t('wallet.withdrawal.fromWallet')}</Typography>
      <Grid container spacing={1.5} sx={{ mb: 2, mt: 0 }}>
        {CURRENCIES.map((c) => {
          const ci = options?.currencies[c];
          const active = currency === c;
          return (
            <Grid item xs={4} key={c}>
              <Button fullWidth variant={active ? 'contained' : 'outlined'} color="secondary" onClick={() => setCurrency(c)} sx={{ flexDirection: 'column', py: 1 }}>
                <Typography variant="body2" fontWeight={700}>{SYM[c]} {c}</Typography>
                <Typography variant="caption">{fmt(ci?.available ?? 0, c)}</Typography>
              </Button>
            </Grid>
          );
        })}
      </Grid>

      {/* Summary */}
      {info && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between"><Typography color="text.secondary">{t('wallet.withdrawal.balance')}</Typography><Typography>{fmt(info.balance, currency)}</Typography></Box>
            {info.blocked > 0 && <Box display="flex" justifyContent="space-between" mt={0.5}><Typography color="text.secondary">{t('wallet.withdrawal.onHold')}</Typography><Typography color="warning.main">-{fmt(info.blocked, currency)}</Typography></Box>}
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between"><Typography fontWeight={700}>{t('wallet.withdrawal.available')}</Typography><Typography fontWeight={700} color="secondary.main">{fmt(info.available, currency)}</Typography></Box>
          </CardContent>
        </Card>
      )}

      {info?.hasPending ? (
        <Alert severity="warning">{t('wallet.withdrawal.hasPending', { currency })}</Alert>
      ) : info && info.available <= 0 ? (
        <Alert severity="info">{t('wallet.withdrawal.noBalance', { currency })}</Alert>
      ) : info && !info.canWithdraw ? (
        <Alert severity="info">{t('wallet.withdrawal.cash.dailyLimitReached')}</Alert>
      ) : info ? (
        <Box display="grid" gap={2}>
          <TextField
            fullWidth type="number" label={t('wallet.withdrawal.cash.amount')} value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: currency === 'LAK' ? 1000 : 1 }}
            helperText={t('wallet.withdrawal.cash.limits', { min: fmt(info.minAmount, currency), max: fmt(info.maxAmount, currency) })}
            InputProps={{ endAdornment: <Button size="small" variant="text" color="secondary" onClick={() => setAmount(String(info.maxAmount))}>{t('wallet.withdrawal.cash.max')}</Button> }}
          />
          <FormControl fullWidth>
            <InputLabel>{t('wallet.withdrawal.cash.branch')}</InputLabel>
            <Select value={stationId} label={t('wallet.withdrawal.cash.branch')} onChange={(e) => setStationId(e.target.value)}>
              {stations.map((st) => <MenuItem key={st.id} value={st.id}>{stationName(st, lang)}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">{t('wallet.withdrawal.cash.branchHint')}</Typography>
        </Box>
      ) : null}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        {t('wallet.withdrawal.cash.disclaimer', { hours: options?.expiryHours ?? 72 })}
      </Typography>

      <Button
        variant="contained" color="secondary" fullWidth sx={{ mt: 2 }} startIcon={<ArrowForward />}
        disabled={!info?.canWithdraw || !amountOk || !stationId}
        onClick={() => setConfirmOpen(true)}
      >
        {amountOk ? `${t('wallet.withdrawal.cash.request')} ${fmt(num, currency)}` : t('wallet.withdrawal.cash.request')}
      </Button>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('wallet.withdrawal.confirmTitle')}</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="space-between" py={0.5}><Typography color="text.secondary">{t('wallet.withdrawal.cash.amount')}</Typography><Typography fontWeight={700}>{fmt(num, currency)}</Typography></Box>
          <Box display="flex" justifyContent="space-between" py={0.5}><Typography color="text.secondary">{t('wallet.withdrawal.cash.branch')}</Typography><Typography>{stationName(station, lang)}</Typography></Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('wallet.withdrawal.cash.bringId')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="secondary" onClick={submit} disabled={submitting} startIcon={submitting ? <CircularProgress size={18} /> : <CheckCircle />}>
            {t('wallet.withdrawal.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// ─── History ─────────────────────────────────────────────────────────────────
const HistoryTab = ({ t, flash }) => {
  const lang = (useTranslation().i18n.language || 'en').split('-')[0];
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getMyWithdrawalRequests(); setRequests(r.data || []); }
    catch (e) { flash(e.message || 'Failed to load', true); }
    finally { setLoading(false); }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (r) => {
    const c = await Swal.fire({
      icon: 'warning', title: t('wallet.withdrawal.cancelConfirm'),
      text: fmt(r.sourceAmount, r.sourceCurrency), showCancelButton: true, confirmButtonColor: '#d33',
    });
    if (!c.isConfirmed) return;
    try { await cancelWithdrawalRequest(r._id); flash(t('wallet.withdrawal.cancelled'), false); await load(); }
    catch (e) { flash(e.message || 'Failed', true); }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress color="secondary" /></Box>;
  if (requests.length === 0) return <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">{t('wallet.withdrawal.noWithdrawals')}</Typography></Paper>;

  return (
    <Grid container spacing={2}>
      {requests.map((r) => (
        <Grid item xs={12} key={r._id}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" color="secondary.main">{fmt(r.sourceAmount, r.sourceCurrency)}</Typography>
                <Chip size="small" label={t(`wallet.withdrawal.status.${r.status}`)} color={STATUS_COLOR[r.status]} />
              </Box>
              {r.isConverted && <Typography variant="body2" color="text.secondary">{t('wallet.withdrawal.receive')} {fmt(r.payoutAmount, r.payoutCurrency)} ({t('wallet.withdrawal.converted')})</Typography>}
              {r.method === 'cash' && r.status === 'pending' && r.pickupCode && (
                <Box sx={{ my: 1.5, p: 2, borderRadius: '12px', bgcolor: 'action.hover', textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">{t('wallet.withdrawal.cash.codeTitle')}</Typography>
                  <Typography variant="h3" fontWeight={800} color="secondary.main" sx={{ letterSpacing: 8, fontFamily: 'monospace' }}>{r.pickupCode}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('wallet.withdrawal.cash.codeHint')}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>{t('wallet.withdrawal.cash.collectAt', { station: stationName(r.station, lang) || r.stationId })}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('wallet.withdrawal.cash.expires', { time: new Date(r.expiresAt).toLocaleString() })}</Typography>
                </Box>
              )}
              {r.method === 'cash' && r.status === 'paid' && (
                <Typography variant="body2" color="text.secondary">{t('wallet.withdrawal.cash.paidAt', { station: r.paidStation?.name || r.paidStationId || '', time: r.paidAt ? new Date(r.paidAt).toLocaleString() : '' })}</Typography>
              )}
              <Typography variant="caption" color="text.secondary">{new Date(r.createdAt).toLocaleString()}</Typography>
              {r.status === 'rejected' && r.rejectionReason && <Alert severity="error" sx={{ mt: 1 }}>{r.rejectionReason}</Alert>}
              {r.status === 'pending' && <Button size="small" color="error" sx={{ mt: 1 }} onClick={() => cancel(r)}>{t('wallet.withdrawal.cancel')}</Button>}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default WalletWithdrawal;
